import {
  FaceLandmarker,
  FilesetResolver,
  PoseLandmarker,
  type FaceLandmarkerResult,
  type PoseLandmarkerResult,
} from "@mediapipe/tasks-vision";
import type { BlendshapeScore, PoseSample, TrackingSample } from "./types";

export async function createFaceLandmarker(): Promise<FaceLandmarker> {
  const fileset = await FilesetResolver.forVisionTasks("/mediapipe");

  try {
    return await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: "/models/face_landmarker.task",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numFaces: 1,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
    });
  } catch (error) {
    console.warn("GPU face landmarker failed, retrying on CPU.", error);
    return FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: "/models/face_landmarker.task",
        delegate: "CPU",
      },
      runningMode: "VIDEO",
      numFaces: 1,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
    });
  }
}

export async function createPoseLandmarker(): Promise<PoseLandmarker> {
  const fileset = await FilesetResolver.forVisionTasks("/mediapipe");

  try {
    return await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: "/models/pose_landmarker_lite.task",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.45,
      minPosePresenceConfidence: 0.45,
      minTrackingConfidence: 0.45,
      outputSegmentationMasks: false,
    });
  } catch (error) {
    console.warn("GPU pose landmarker failed, retrying on CPU.", error);
    return PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: "/models/pose_landmarker_lite.task",
        delegate: "CPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.45,
      minPosePresenceConfidence: 0.45,
      minTrackingConfidence: 0.45,
      outputSegmentationMasks: false,
    });
  }
}

export function resultToSample(result: FaceLandmarkerResult): TrackingSample | null {
  const landmarks = result.faceLandmarks?.[0];
  if (!landmarks?.length) return null;

  const blendshapes: BlendshapeScore[] =
    result.faceBlendshapes?.[0]?.categories?.map((category) => ({
      name: category.categoryName,
      score: category.score,
    })) ?? [];

  return {
    landmarks,
    blendshapes,
    matrix: result.facialTransformationMatrixes?.[0],
  };
}

export function resultToPoseSample(result: PoseLandmarkerResult): PoseSample | null {
  const landmarks = result.landmarks?.[0];
  if (!landmarks?.length) return null;

  return {
    landmarks,
  };
}
